import { Check, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

import { Button } from "@/components/ui/v2/button";
import { cn } from "@/lib/utils/cn";
import { TranslationFunction } from "@/types/translation-function";

// Types
type MonthYearSelectorProps = {
  value: [string, string]; // [year, month] - Year in YYYY format, Month in MM format (01-12)
  onChange: (value: [string, string]) => void;
  minDate?: [string, string]; // Minimum selectable [year, month]
  maxDate?: [string, string]; // Maximum selectable [year, month]
};

type YearSelectorProps = {
  year: number;
  yearOptions: number[];
  onYearSelect: (year: number) => void;
  handlePrev: () => void;
  handleNext: () => void;
  isPrevDisabled?: boolean;
  isNextDisabled?: boolean;
  minYear?: number;
  maxYear?: number;
  currentMonth: number;
  maxMonth?: number;
};

type MonthSelectorProps = {
  month: number;
  monthOptions: Array<{ value: number; label: string }>;
  onMonthSelect: (month: number) => void;
  handlePrev: () => void;
  handleNext: () => void;
  isPrevDisabled?: boolean;
  isNextDisabled?: boolean;
  minYear?: number;
  maxYear?: number;
  currentYear: number;
  minMonth?: number;
  maxMonth?: number;
};

type MonthOption = {
  value: number;
  label: string;
};

// Helper functions
function getMonthNames(t: TranslationFunction): string[] {
  return [
    t("common.months.january", "January"),
    t("common.months.february", "February"),
    t("common.months.march", "March"),
    t("common.months.april", "April"),
    t("common.months.may", "May"),
    t("common.months.june", "June"),
    t("common.months.july", "July"),
    t("common.months.august", "August"),
    t("common.months.september", "September"),
    t("common.months.october", "October"),
    t("common.months.november", "November"),
    t("common.months.december", "December"),
  ];
}

function generateYearOptions(minYear?: number, maxYear?: number): number[] {
  const currentYear = new Date().getFullYear();
  const startYear = Math.max(2000, minYear ?? 2000);
  const endYear = maxYear ?? currentYear + 10;
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) {
    years.push(y);
  }
  // Reverse to show most recent year first
  return years.reverse();
}

function generateMonthOptions(monthNames: string[]): MonthOption[] {
  return monthNames.map((name, index) => ({
    value: index + 1,
    label: name,
  }));
}

function MonthYearSelector({
  value,
  onChange,
  minDate,
  maxDate,
}: MonthYearSelectorProps) {
  const { t } = useTranslation();

  const [yearStr, monthStr] = value;
  const year = Number.parseInt(yearStr, 10) || new Date().getFullYear();
  const month = Math.max(1, Math.min(12, Number.parseInt(monthStr, 10) || 1));

  // Parse min/max constraints
  const minYear = minDate ? Number.parseInt(minDate[0], 10) : undefined;
  const minMonth = minDate ? Number.parseInt(minDate[1], 10) : undefined;
  const maxYear = maxDate ? Number.parseInt(maxDate[0], 10) : undefined;
  const maxMonth = maxDate ? Number.parseInt(maxDate[1], 10) : undefined;

  // Month names
  const monthNames = useMemo(() => getMonthNames(t), [t]);

  // Handle month change
  const handleMonthChange = (direction: "next" | "prev") => {
    // Guard: Check max boundary for next
    if (direction === "next") {
      if (maxYear !== undefined && year === maxYear && month === maxMonth) {
        return;
      }
    }

    // Guard: Check min boundary for prev
    if (direction === "prev") {
      if (minYear !== undefined && year === minYear && month === minMonth) {
        return;
      }
    }

    // Calculate new month and year
    let newMonth: number;
    let newYear: number;

    if (direction === "next") {
      if (month === 12) {
        newMonth = 1;
        newYear = year + 1;
      } else {
        newMonth = month + 1;
        newYear = year;
      }
    } else {
      if (month === 1) {
        newMonth = 12;
        newYear = year - 1;
      } else {
        newMonth = month - 1;
        newYear = year;
      }
    }

    // Convert to strings and format
    const newYearStr = newYear.toString();
    const newMonthStr = newMonth.toString().padStart(2, "0");
    const newValue: [string, string] = [newYearStr, newMonthStr];

    onChange(newValue);
  };

  // Handle year change
  const handleYearChange = (direction: "next" | "prev") => {
    // Check boundaries for year change
    if (direction === "next" && maxYear !== undefined && year >= maxYear) {
      return; // Can't go further
    }

    if (direction === "prev" && minYear !== undefined && year <= minYear) {
      return; // Can't go further
    }

    const newYear = direction === "next" ? year + 1 : year - 1;

    // Convert to strings and format
    const newYearStr = newYear.toString();
    const newMonthStr = month.toString().padStart(2, "0");
    const newValue: [string, string] = [newYearStr, newMonthStr];

    onChange(newValue);
  };

  // Determine if buttons should be disabled
  const isPrevMonthDisabled =
    minYear !== undefined && year === minYear && month === minMonth;
  const isNextMonthDisabled =
    maxYear !== undefined && year === maxYear && month === maxMonth;
  const isPrevYearDisabled = minYear !== undefined && year <= minYear;
  const isNextYearDisabled =
    maxYear !== undefined &&
    (year >= maxYear ||
      (maxMonth !== undefined && year === maxYear - 1 && month > maxMonth));

  // Generate year options (most recent first)
  const yearOptions = useMemo(
    () => generateYearOptions(minYear, maxYear),
    [minYear, maxYear]
  );

  // Generate month options
  const monthOptions = useMemo(
    () => generateMonthOptions(monthNames),
    [monthNames]
  );

  return (
    <div className="flex gap-2.5 items-center justify-center">
      <YearSelector
        year={year}
        yearOptions={yearOptions}
        onYearSelect={(selectedYear) => {
          const newYearStr = selectedYear.toString();
          const newMonthStr = month.toString().padStart(2, "0");
          onChange([newYearStr, newMonthStr]);
        }}
        handlePrev={() => handleYearChange("prev")}
        handleNext={() => handleYearChange("next")}
        isPrevDisabled={isPrevYearDisabled}
        isNextDisabled={isNextYearDisabled}
        minYear={minYear}
        maxYear={maxYear}
        currentMonth={month}
        maxMonth={maxMonth}
      />

      <MonthSelector
        month={month}
        monthOptions={monthOptions}
        onMonthSelect={(selectedMonth) => {
          const newYearStr = year.toString();
          const newMonthStr = selectedMonth.toString().padStart(2, "0");
          onChange([newYearStr, newMonthStr]);
        }}
        handlePrev={() => handleMonthChange("prev")}
        handleNext={() => handleMonthChange("next")}
        isPrevDisabled={isPrevMonthDisabled}
        isNextDisabled={isNextMonthDisabled}
        minYear={minYear}
        maxYear={maxYear}
        currentYear={year}
        minMonth={minMonth}
        maxMonth={maxMonth}
      />
    </div>
  );
}

function YearSelector({
  year,
  yearOptions,
  onYearSelect,
  handlePrev,
  handleNext,
  isPrevDisabled,
  isNextDisabled,
  minYear,
  maxYear,
  currentMonth,
  maxMonth,
}: YearSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const filteredYears = useMemo(() => {
    if (!searchTerm) return yearOptions;
    const search = searchTerm.toLowerCase();
    return yearOptions.filter((y) => y.toString().includes(search));
  }, [yearOptions, searchTerm]);

  // Scroll to selected year when popover opens
  useEffect(() => {
    if (open && !searchTerm && listRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const selectedElement = listRef.current?.querySelector(
          `[data-value="${year}"]`
        ) as HTMLElement;
        if (selectedElement) {
          selectedElement.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [open, searchTerm, year]);

  const handleSelect = (selectedYear: number) => {
    // Check if the selected year is valid with current month constraints
    if (
      maxYear !== undefined &&
      selectedYear === maxYear &&
      maxMonth !== undefined &&
      currentMonth > maxMonth
    ) {
      // If selecting max year and current month exceeds max month, don't allow
      return;
    }
    if (minYear !== undefined && selectedYear === minYear && currentMonth < 1) {
      return;
    }
    onYearSelect(selectedYear);
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="flex gap-2 items-center max-h-9 px-2.5 py-2.5 border border-border rounded-lg bg-background/70 backdrop-blur-xl text-popover-foreground">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={isPrevDisabled ? undefined : handlePrev}
        disabled={isPrevDisabled}
        className={cn(
          "h-auto w-auto p-0",
          isPrevDisabled && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Previous year"
      >
        <ChevronLeftIcon className="size-6" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="min-w-[76px] font-normal leading-6 text-center hover-tint-primary rounded-lg px-2  transition-colors cursor-pointer"
            aria-label="Select year"
          >
            {year}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0 bg-transparent" align="start">
          <Command shouldFilter={false}>
            <div className="relative border-b w-full">
              <CommandInput
                placeholder={t(
                  "common.monthYearSelector.searchYear",
                  "Search year..."
                )}
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
            </div>
            <CommandList ref={listRef}>
              {filteredYears.length === 0 ? (
                <CommandEmpty>
                  {t("common.monthYearSelector.noYearFound", "No year found.")}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredYears.map((y) => (
                    <CommandItem
                      key={y}
                      value={y.toString()}
                      onSelect={() => handleSelect(y)}
                      data-value={y}
                    >
                      {y}
                      <Check
                        className={cn(
                          "ml-auto h-3 w-3",
                          year === y ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={isNextDisabled ? undefined : handleNext}
        disabled={isNextDisabled}
        className={cn(
          "h-auto w-auto p-0",
          isNextDisabled && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Next year"
      >
        <ChevronRightIcon className="size-6" />
      </Button>
    </div>
  );
}

function MonthSelector({
  month,
  monthOptions,
  onMonthSelect,
  handlePrev,
  handleNext,
  isPrevDisabled,
  isNextDisabled,
  minYear,
  maxYear,
  currentYear,
  minMonth,
  maxMonth,
}: MonthSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const filteredMonths = useMemo(() => {
    let available = monthOptions;

    // Filter based on constraints
    if (
      minYear !== undefined &&
      currentYear === minYear &&
      minMonth !== undefined
    ) {
      available = available.filter((m) => m.value >= minMonth);
    }
    if (
      maxYear !== undefined &&
      currentYear === maxYear &&
      maxMonth !== undefined
    ) {
      available = available.filter((m) => m.value <= maxMonth);
    }

    if (!searchTerm) return available;
    const search = searchTerm.toLowerCase();
    return available.filter((m) => m.label.toLowerCase().includes(search));
  }, [
    monthOptions,
    searchTerm,
    minYear,
    maxYear,
    currentYear,
    minMonth,
    maxMonth,
  ]);

  const handleSelect = (selectedMonth: number) => {
    // Check if the selected month is valid with current year constraints
    if (
      maxYear !== undefined &&
      currentYear === maxYear &&
      maxMonth !== undefined &&
      selectedMonth > maxMonth
    ) {
      return;
    }
    if (
      minYear !== undefined &&
      currentYear === minYear &&
      minMonth !== undefined &&
      selectedMonth < minMonth
    ) {
      return;
    }
    onMonthSelect(selectedMonth);
    setOpen(false);
    setSearchTerm("");
  };

  const currentMonthLabel = monthOptions[month - 1]?.label || "";

  return (
    <div className="flex gap-2 items-center max-h-9 px-2.5 py-2.5 border border-border rounded-lg bg-background/70 backdrop-blur-xl text-popover-foreground">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={isPrevDisabled ? undefined : handlePrev}
        disabled={isPrevDisabled}
        className={cn(
          "h-auto w-auto p-0",
          isPrevDisabled && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Previous month"
      >
        <ChevronLeftIcon className="size-6" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="min-w-[76px]  font-normal leading-6 text-center hover-tint-primary rounded-lg px-2  transition-colors cursor-pointer"
            aria-label="Select month"
          >
            {currentMonthLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0 bg-transparent" align="start">
          <Command shouldFilter={false}>
            <div className="relative border-b w-full">
              <CommandInput
                placeholder={t(
                  "common.monthYearSelector.searchMonth",
                  "Search month..."
                )}
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
            </div>
            <CommandList ref={listRef}>
              {filteredMonths.length === 0 ? (
                <CommandEmpty>
                  {t(
                    "common.monthYearSelector.noMonthFound",
                    "No month found."
                  )}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredMonths.map((m) => (
                    <CommandItem
                      key={m.value}
                      value={m.value.toString()}
                      onSelect={() => handleSelect(m.value)}
                      data-value={m.value}
                    >
                      {m.label}
                      <Check
                        className={cn(
                          "ml-auto h-3 w-3",
                          month === m.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={isNextDisabled ? undefined : handleNext}
        disabled={isNextDisabled}
        className={cn(
          "h-auto w-auto p-0",
          isNextDisabled && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Next month"
      >
        <ChevronRightIcon className="size-6" />
      </Button>
    </div>
  );
}

export { MonthYearSelector };
export type {
  MonthOption,
  MonthSelectorProps,
  MonthYearSelectorProps,
  YearSelectorProps,
};
